import { useContext, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Select,
  MenuItem,
  ListSubheader,
  FormControl,
  InputLabel,
} from "@mui/material";
import ResponsiveTopBar from "../components/ResponsiveTopBar";
import MobileNavDrawer from "../components/MobileNavDrawer";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { DeviceContext } from "../context/DeviceContext";
import { PositionContext } from "../context/PositionContext";
import CloseIcon from "@mui/icons-material/Close";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { arrayMove } from "@dnd-kit/sortable";
import { SortableItem } from "../components/SortableItem";
import VideoTrimmer from "../components/VideoTrimmer";
import { CommunitySearch } from "../components/CommunitySearchFilter";
import { TagSearch } from "../components/TagSearch";
import TickerSearch from "../components/TickerSearch";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { uploadFile } from "../utils/uploadFile";

const DL = {
  bgDark: "#0f1419",
  bgMedium: "#1a1f26",
  bgLight: "#242930",
  green: "#62f481",
  greenEmerald: "#44aa5a",
  textPrimary: "#f9f9cf",
  textSecondary: "#99a1af",
  textMuted: "#6a7282",
  border: "rgba(255,255,255,0.08)",
  borderSubtle: "rgba(255,255,255,0.15)",
  borderGreen: "rgba(98,244,129,0.3)",
  divider: "rgba(98,244,129,0.2)",
  red: "#ff6467",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: DL.bgLight,
    borderRadius: "8px",
    "& fieldset": { borderColor: DL.border },
    "&:hover fieldset": { borderColor: DL.borderSubtle },
    "&.Mui-focused fieldset": { borderColor: DL.green },
  },
  "& .MuiInputBase-input": { color: DL.textPrimary, fontSize: "0.9rem" },
  "& .MuiInputLabel-root": { color: DL.textMuted, fontSize: "0.9rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: DL.green },
  "& .MuiFormHelperText-root": { color: DL.red },
  "& .MuiInputBase-input::placeholder": { color: DL.textMuted },
};

const primaryBtnSx = {
  background: "linear-gradient(to right, #10b981, #059669)",
  color: "#fff",
  borderRadius: "8px",
  px: 3,
  py: 1,
  fontWeight: 700,
  fontSize: "0.875rem",
  textTransform: "none",
  boxShadow: "0px 2px 8px rgba(98,244,129,0.4)",
  "&:hover": { filter: "brightness(110%)" },
  "&:disabled": { opacity: 0.4 },
  transition: "all 0.2s ease",
};

const positionSelectSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: DL.bgLight,
    borderRadius: "8px",
    "& fieldset": { borderColor: DL.border },
    "&:hover fieldset": { borderColor: DL.borderSubtle },
    "&.Mui-focused fieldset": { borderColor: DL.green },
  },
  "& .MuiSelect-select": { color: DL.textPrimary, fontSize: "0.95rem", py: 1.4 },
  "& .MuiInputLabel-root": { color: DL.textMuted, fontSize: "0.95rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: DL.green },
  "& .MuiSvgIcon-root": { color: DL.textMuted },
};

const positionMenuProps = {
  PaperProps: {
    sx: {
      backgroundColor: DL.bgMedium,
      border: `1px solid ${DL.border}`,
      borderRadius: "10px",
      mt: 0.5,
      "& .MuiMenuItem-root": {
        fontSize: "0.95rem",
        color: DL.textPrimary,
        minHeight: 44,
        "&:hover": { backgroundColor: DL.bgLight },
        "&.Mui-selected": { backgroundColor: "rgba(98,244,129,0.1)", color: DL.green },
        "&.Mui-selected:hover": { backgroundColor: "rgba(98,244,129,0.15)" },
      },
      "& .MuiListSubheader-root": {
        backgroundColor: DL.bgDark,
        color: DL.textMuted,
        fontSize: "0.75rem",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        lineHeight: "32px",
      },
    },
  },
};

const secondaryBtnSx = {
  backgroundColor: DL.bgLight,
  border: `1px solid ${DL.textMuted}`,
  color: DL.textPrimary,
  borderRadius: "8px",
  px: 2.5,
  py: 1,
  fontWeight: 600,
  fontSize: "0.875rem",
  textTransform: "none",
  "&:hover": { borderColor: DL.textSecondary, backgroundColor: "rgba(255,255,255,0.06)" },
  "&:disabled": { opacity: 0.3 },
  transition: "all 0.2s ease",
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const MediaThumbnails = ({
  mediaFiles,
  selectedMediaIndex,
  setSelectedMediaIndex,
  handleDeleteMedia,
  handleReorder,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    let oldIndex = mediaFiles.findIndex((file) => file.id === active.id);
    let newIndex = mediaFiles.findIndex((file) => file.id === over.id);
    if (oldIndex === -1 && active.data?.current?.index !== undefined) oldIndex = active.data.current.index;
    if (newIndex === -1 && over.data?.current?.index !== undefined) newIndex = over.data.current.index;
    if (oldIndex >= 0 && newIndex >= 0) handleReorder(oldIndex, newIndex);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={mediaFiles.map((file) => file.id)} strategy={horizontalListSortingStrategy}>
        <div style={{
          display: "flex",
          flexDirection: "row",
          overflowX: "auto",
          minHeight: 100,
          padding: 8,
          backgroundColor: DL.bgDark,
          borderRadius: 8,
        }}>
          {mediaFiles.map((media, index) => (
            <SortableItem
              key={media.id}
              id={media.id}
              media={media}
              index={index}
              selectedMediaIndex={selectedMediaIndex}
              setSelectedMediaIndex={setSelectedMediaIndex}
              handleDeleteMedia={handleDeleteMedia}
              theme={{
                palette: {
                  primary: { main: DL.green },
                  error: { main: DL.red, dark: "#cc3336" },
                  divider: DL.border,
                  action: { hover: "rgba(255,255,255,0.06)" },
                  background: { paper: DL.bgLight, default: DL.bgDark, gray: DL.bgLight },
                  text: { secondary: DL.textSecondary },
                },
              }}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

function CreatePost() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [communityName, setCommunityName] = useState("");
  const [tags, setTags] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { isMobile } = useContext(DeviceContext);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);

  const handleReorder = (oldIndex, newIndex) => {
    setMediaFiles(arrayMove(mediaFiles, oldIndex, newIndex));
    setCrops(arrayMove(crops, oldIndex, newIndex));
    if (croppedFiles.length > 0) setCroppedFiles(arrayMove(croppedFiles, oldIndex, newIndex));
    if (trimmedVideos.length > 0) setTrimmedVideos(arrayMove(trimmedVideos, oldIndex, newIndex));
    if (selectedMediaIndex === oldIndex) {
      setSelectedMediaIndex(newIndex);
    } else if (selectedMediaIndex > oldIndex && selectedMediaIndex <= newIndex) {
      setSelectedMediaIndex(selectedMediaIndex - 1);
    } else if (selectedMediaIndex < oldIndex && selectedMediaIndex >= newIndex) {
      setSelectedMediaIndex(selectedMediaIndex + 1);
    }
  };

  // Position-related states
  const { positions: savedPositions, addPosition: savePosition } = useContext(PositionContext);
  const [positions, setPositions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showPositionForm, setShowPositionForm] = useState(false);
  const [currentPosition, setCurrentPosition] = useState({
    type: "", ticker: "", entryPrice: "", quantity: "",
    strikePrice: "", lowerPutStrike: "", upperPutStrike: "",
    lowerCallStrike: "", upperCallStrike: "",
    entryDate: new Date().toISOString().split("T")[0],
  });

  const handleAddPosition = async () => {
    if (currentPosition.type && currentPosition.ticker) {
      const newPos = await savePosition(currentPosition);
      if (!newPos) return;
      setPositions([...positions, newPos]);
      setCurrentPosition({
        type: "", ticker: "", entryPrice: "", quantity: "",
        strikePrice: "", lowerPutStrike: "", upperPutStrike: "",
        lowerCallStrike: "", upperCallStrike: "",
        entryDate: new Date().toISOString().split("T")[0],
      });
      setShowPositionForm(false);
    }
  };

  const handleRemovePosition = (id) => setPositions(positions.filter((p) => getPositionId(p) !== id));

  const handleToggleExisting = (pos) => {
    const posId = getPositionId(pos);
    const exists = positions.some((p) => getPositionId(p) === posId);
    setPositions(exists ? positions.filter((p) => getPositionId(p) !== posId) : [...positions, pos]);
  };

  const formatPositionType = (type) => {
    if (!type) return "Unknown";
    return type.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  const getPositionType = (pos) => pos.type || pos.positionType;
  const getPositionId = (pos) => pos.positionId || pos.id;

  const getPositionDetails = (pos) => {
    let details = [];
    const type = getPositionType(pos) || "";
    if (pos.entryPrice) details.push(`Entry: $${pos.entryPrice}`);
    if (pos.quantity) {
      if (type.includes("crypto")) details.push(`Quantity: ${pos.quantity} ${pos.ticker}`);
      else if (type.includes("shares") || type === "short-sell") details.push(`Quantity: ${pos.quantity} shares`);
      else if (type.includes("call") || type.includes("put")) details.push(`Contracts: ${pos.quantity}`);
    }
    if (pos.strikePrice) details.push(`Strike: $${pos.strikePrice}`);
    return details.join(" | ");
  };

  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Basics", "Content", "Media", "Position"];

  const [croppedFiles, setCroppedFiles] = useState([]);
  const [trimmedVideos, setTrimmedVideos] = useState([]);
  const [crops, setCrops] = useState([]);

  const handleMediaUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length + mediaFiles.length > 5) { setErrorMessage("You can only upload up to 5 media files."); return; }
    const largeVideoFiles = files.filter((f) => f.type.startsWith("video/") && f.size > 10 * 1024 * 1024);
    if (largeVideoFiles.length > 0) setErrorMessage("Some video files are larger than 10MB. You may need to trim them.");
    const updatedFiles = [...mediaFiles];
    const updatedCrops = [...crops];
    files.forEach((file) => {
      updatedFiles.push({
        id: `file-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        file, type: file.type.startsWith("image/") ? "image" : "video",
        preview: URL.createObjectURL(file), size: file.size,
      });
      updatedCrops.push({ unit: "%", width: 100, height: 100, x: 0, y: 0 });
    });
    setMediaFiles(updatedFiles);
    setCrops(updatedCrops);
    setErrorMessage("");
  };

  const handleDeleteMedia = (index) => {
    const updatedFiles = [...mediaFiles];
    const updatedCrops = [...crops];
    const updatedCroppedFiles = [...croppedFiles];
    const updatedTrimmedVideos = [...trimmedVideos];
    URL.revokeObjectURL(updatedFiles[index].preview);
    if (updatedCroppedFiles[index]) URL.revokeObjectURL(updatedCroppedFiles[index]);
    updatedFiles.splice(index, 1);
    updatedCrops.splice(index, 1);
    if (updatedCroppedFiles[index]) updatedCroppedFiles.splice(index, 1);
    if (updatedTrimmedVideos[index]) updatedTrimmedVideos.splice(index, 1);
    if (selectedMediaIndex >= updatedFiles.length) setSelectedMediaIndex(Math.max(0, updatedFiles.length - 1));
    setMediaFiles(updatedFiles);
    setCrops(updatedCrops);
    setCroppedFiles(updatedCroppedFiles);
    setTrimmedVideos(updatedTrimmedVideos);
  };

  const onCropChange = (crop, index) => {
    const updatedCrops = [...crops];
    updatedCrops[index] = crop;
    setCrops(updatedCrops);
  };

  const onImageLoad = (_, index) => {
    const updatedCrops = [...crops];
    updatedCrops[index] = { unit: "%", width: 100, height: 100, x: 0, y: 0 };
    setCrops(updatedCrops);
  };

  const getCroppedImg = (imageElement, crop, fileName) => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const scaleX = imageElement.naturalWidth / imageElement.width;
      const scaleY = imageElement.naturalHeight / imageElement.height;
      canvas.width = crop.width * scaleX;
      canvas.height = crop.height * scaleY;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(imageElement, crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY, 0, 0, crop.width * scaleX, crop.height * scaleY);
      canvas.toBlob((blob) => { if (blob) { blob.name = fileName; resolve(URL.createObjectURL(blob)); } }, "image/jpeg");
    });
  };

  const completeCrop = async (index) => {
    if (!crops[index]) return;
    const imageRef = document.getElementById(`image-${index}`);
    if (!imageRef || !crops[index]?.width || !crops[index]?.height) return;
    try {
      const croppedImage = await getCroppedImg(imageRef, crops[index], `cropped-image-${index}.jpg`);
      const updatedCroppedFiles = [...croppedFiles];
      updatedCroppedFiles[index] = croppedImage;
      setCroppedFiles(updatedCroppedFiles);
    } catch (error) {
      console.error("Error generating crop:", error);
    }
  };

  const handleVideoTrim = (startTime, endTime, index) => {
    const updatedTrimmedVideos = [...trimmedVideos];
    updatedTrimmedVideos[index] = { startTime, endTime };
    setTrimmedVideos(updatedTrimmedVideos);
  };

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleSubmit = async () => {
    setUploading(true);
    const API_BASE_URL = process.env.REACT_APP_SC_API_BASE_URL || "http://localhost:8080";
    try {
      const mediaUrls = await Promise.all(
        mediaFiles.map(async (mediaItem, index) => {
          let file, fileName, contentType;
          if (mediaItem.type === "image") {
            if (croppedFiles[index]) {
              const response = await fetch(croppedFiles[index]);
              file = await response.blob();
              fileName = `image-${index}.jpg`;
              contentType = "image/jpeg";
            } else {
              file = mediaItem.file;
              fileName = mediaItem.file.name;
            }
          } else if (mediaItem.type === "video") {
            file = mediaItem.file;
            fileName = mediaItem.file.name;
          }
          const fileKey = await uploadFile(file, fileName, user.token, contentType);
          return { url: fileKey, type: mediaItem.type };
        })
      );
      const postData = {
        userId: user.id,
        communityName,
        title,
        content,
        imageUrls: mediaUrls.map((m) => m.url),
        tags,
        positionIds: positions.map((p) => getPositionId(p)),
      };
      const postResponse = await fetch(`${API_BASE_URL}/api/posts/createPost`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${user.token}` },
        body: JSON.stringify(postData),
      });
      if (!postResponse.ok) throw new Error("Failed to create post");
      const createdPost = await postResponse.json();
      mediaFiles.forEach((m) => URL.revokeObjectURL(m.preview));
      croppedFiles.forEach((url) => { if (url) URL.revokeObjectURL(url); });
      navigate(`/post/${createdPost.postId || createdPost.id}`);
    } catch (error) {
      console.error("Error creating post:", error);
      setErrorMessage(`Error creating post: ${error.message || "Please try again."}`);
      setUploading(false);
    }
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 0: return !!title;
      case 1: return content.length > 0;
      case 2: return true;
      case 3: return positions.length > 0;
      default: return false;
    }
  };

  // ── Step content ─────────────────────────────────────────────────────────────
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <CommunitySearch communityName={communityName} setCommunityName={setCommunityName} />
            <TagSearch tags={tags} setTags={setTags} />
            <TextField
              label="Title"
              variant="outlined"
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={inputSx}
            />
          </Box>
        );

      case 1:
        return (
          <TextField
            label="Content"
            variant="outlined"
            fullWidth
            multiline
            rows={15}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            sx={inputSx}
          />
        );

      case 2:
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <input type="file" hidden multiple accept="image/*,video/*" onChange={handleMediaUpload} id="media-upload" />
              <label htmlFor="media-upload">
                <Button
                  component="span"
                  sx={primaryBtnSx}>
                  Upload Media
                </Button>
              </label>
            </Box>

            {errorMessage && (
              <Alert severity="error" sx={{ borderRadius: "8px", backgroundColor: "rgba(255,100,103,0.1)", color: DL.red, border: "1px solid rgba(255,100,103,0.25)", "& .MuiAlert-icon": { color: DL.red } }}>
                {errorMessage}
              </Alert>
            )}

            {mediaFiles.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: "0.8rem", color: DL.textMuted, mb: 1, textAlign: "center" }}>
                  Drag to reorder
                </Typography>
                <MediaThumbnails
                  mediaFiles={mediaFiles}
                  selectedMediaIndex={selectedMediaIndex}
                  setSelectedMediaIndex={setSelectedMediaIndex}
                  handleDeleteMedia={handleDeleteMedia}
                  setMediaFiles={setMediaFiles}
                  handleReorder={handleReorder}
                />
              </Box>
            )}

            {mediaFiles.length === 0 && (
              <Box sx={{
                border: `1px dashed ${DL.borderGreen}`,
                borderRadius: "12px",
                height: 120,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: DL.textMuted,
                fontSize: "0.875rem",
              }}>
                No media uploaded yet
              </Box>
            )}

            {mediaFiles.length > 0 && (
              <Box
                sx={{
                  backgroundColor: DL.bgDark,
                  border: `1px solid ${DL.border}`,
                  borderRadius: "12px",
                  overflow: "hidden",
                }}>
                <Box sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 1.5,
                  borderBottom: `1px solid ${DL.border}`,
                }}>
                  <Typography sx={{ fontSize: "0.85rem", color: DL.textSecondary, fontWeight: 600 }}>
                    {`Editing ${mediaFiles[selectedMediaIndex]?.type === "image" ? "Image" : "Video"} ${selectedMediaIndex + 1} of ${mediaFiles.length}`}
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: DL.textMuted }}>
                    {formatFileSize(mediaFiles[selectedMediaIndex]?.size || 0)}
                  </Typography>
                </Box>

                <Box sx={{ p: 2 }}>
                  {mediaFiles[selectedMediaIndex]?.type === "image" ? (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                      <ReactCrop
                        crop={crops[selectedMediaIndex] || { unit: "%", width: 100, height: 100, x: 0, y: 0 }}
                        onChange={(newCrop) => onCropChange(newCrop, selectedMediaIndex)}
                        circularCrop={false}>
                        <img
                          id={`image-${selectedMediaIndex}`}
                          src={mediaFiles[selectedMediaIndex].preview}
                          alt="Selected media"
                          style={{ maxWidth: "100%", maxHeight: "500px" }}
                          onLoad={(e) => onImageLoad(e, selectedMediaIndex)}
                        />
                      </ReactCrop>
                      <Button onClick={() => completeCrop(selectedMediaIndex)} sx={primaryBtnSx}>
                        Apply Crop
                      </Button>
                      {croppedFiles[selectedMediaIndex] && (
                        <Box sx={{ textAlign: "center", width: "100%" }}>
                          <Typography sx={{ fontSize: "0.8rem", color: DL.textMuted, mb: 1 }}>Preview:</Typography>
                          <img src={croppedFiles[selectedMediaIndex]} alt="Cropped preview" style={{ maxWidth: "100%", maxHeight: "200px", borderRadius: 8 }} />
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <VideoTrimmer
                      videoSrc={mediaFiles[selectedMediaIndex].preview}
                      onTrimComplete={(startTime, endTime) => handleVideoTrim(startTime, endTime, selectedMediaIndex)}
                    />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        );

      case 3:
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: DL.green }}>
              Add Trading Positions (Optional)
            </Typography>

            <TextField
              label="Search Positions"
              variant="outlined"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by ticker or position type..."
              sx={inputSx}
            />

            {savedPositions.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: "0.85rem", color: DL.textSecondary, fontWeight: 600, mb: 1 }}>
                  Your Saved Positions
                </Typography>
                {savedPositions
                  .filter((p) =>
                    p.ticker?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    formatPositionType(getPositionType(p)).toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((pos) => (
                    <Box
                      key={getPositionId(pos)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: DL.bgLight,
                        border: `1px solid ${DL.border}`,
                        borderRadius: "8px",
                        px: 1.5,
                        py: 0.5,
                        mb: 1,
                      }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={positions.some((p) => getPositionId(p) === getPositionId(pos))}
                            onChange={() => handleToggleExisting(pos)}
                            sx={{ color: DL.textMuted, "&.Mui-checked": { color: DL.green } }}
                          />
                        }
                        label={
                          <Typography sx={{ fontSize: "0.875rem", color: DL.textPrimary }}>
                            {formatPositionType(getPositionType(pos))} — {pos.ticker}
                          </Typography>
                        }
                      />
                    </Box>
                  ))}
              </Box>
            )}

            {positions.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: "0.85rem", color: DL.textSecondary, fontWeight: 600, mb: 1 }}>
                  Selected Positions
                </Typography>
                {positions.map((pos) => (
                  <Box
                    key={getPositionId(pos)}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      backgroundColor: DL.bgLight,
                      border: `1px solid ${DL.borderGreen}`,
                      borderRadius: "8px",
                      px: 2,
                      py: 1.5,
                      mb: 1,
                    }}>
                    <Box>
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: DL.textPrimary }}>
                        {formatPositionType(getPositionType(pos))} — {pos.ticker}
                      </Typography>
                      <Typography sx={{ fontSize: "0.8rem", color: DL.textSecondary }}>
                        {getPositionDetails(pos)}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => handleRemovePosition(getPositionId(pos))} sx={{ color: DL.red }}>
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {!showPositionForm && (
              <Button
                onClick={() => setShowPositionForm(true)}
                sx={{
                  ...secondaryBtnSx,
                  alignSelf: "flex-start",
                  borderColor: DL.borderGreen,
                  color: DL.green,
                  "&:hover": { backgroundColor: "rgba(98,244,129,0.06)", borderColor: DL.green },
                }}>
                + Add New Position
              </Button>
            )}

            {showPositionForm && (
              <Box
                sx={{
                  backgroundColor: DL.bgDark,
                  border: `1px solid ${DL.border}`,
                  borderRadius: "12px",
                  p: 2,
                }}>
                <Typography sx={{ fontSize: "0.85rem", color: DL.textSecondary, fontWeight: 600, mb: 2 }}>
                  New Position Details
                </Typography>
                <Stack spacing={2}>
                  <FormControl fullWidth sx={positionSelectSx}>
                    <InputLabel>Position Type</InputLabel>
                    <Select
                      value={currentPosition.type}
                      onChange={(e) => setCurrentPosition({ ...currentPosition, type: e.target.value })}
                      label="Position Type"
                      MenuProps={positionMenuProps}>
                      <ListSubheader>Stock Positions</ListSubheader>
                      <MenuItem value="buy-shares">Buy (Shares)</MenuItem>
                      <MenuItem value="short-sell">Short Sell</MenuItem>
                      <ListSubheader>Options Positions</ListSubheader>
                      <MenuItem value="buy-call">Buy Call</MenuItem>
                      <MenuItem value="sell-put">Sell Put</MenuItem>
                      <MenuItem value="covered-call">Covered Call</MenuItem>
                      <MenuItem value="iron-condor">Iron Condor</MenuItem>
                      <MenuItem value="straddle">Straddle</MenuItem>
                      <ListSubheader>Crypto Positions</ListSubheader>
                      <MenuItem value="buy-crypto">Buy Crypto</MenuItem>
                      <MenuItem value="sell-crypto">Sell Crypto</MenuItem>
                    </Select>
                  </FormControl>

                  <TickerSearch
                    ticker={currentPosition.ticker}
                    setTicker={(ticker) => setCurrentPosition({ ...currentPosition, ticker })}
                    label="Ticker Symbol"
                    placeholder="Search for ticker (e.g., AAPL, BTC, SPY)"
                  />

                  <TextField
                    label="Entry Price"
                    variant="outlined"
                    type="number"
                    fullWidth
                    value={currentPosition.entryPrice}
                    onChange={(e) => setCurrentPosition({ ...currentPosition, entryPrice: e.target.value })}
                    InputProps={{ startAdornment: <Box component="span" sx={{ mr: 1, color: DL.textMuted }}>$</Box> }}
                    sx={inputSx}
                  />

                  <TextField
                    label="Date Acquired"
                    variant="outlined"
                    type="date"
                    fullWidth
                    value={currentPosition.entryDate}
                    onChange={(e) => setCurrentPosition({ ...currentPosition, entryDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={inputSx}
                  />

                  {currentPosition.type && (
                    <>
                      {currentPosition.type.includes("crypto") ? (
                        <TextField
                          label="Quantity"
                          variant="outlined"
                          type="number"
                          fullWidth
                          value={currentPosition.quantity}
                          onChange={(e) => setCurrentPosition({ ...currentPosition, quantity: e.target.value })}
                          InputProps={{ endAdornment: <Box component="span" sx={{ ml: 1, color: DL.textMuted }}>{currentPosition.ticker}</Box> }}
                          sx={inputSx}
                        />
                      ) : currentPosition.type.includes("shares") || currentPosition.type === "short-sell" ? (
                        <TextField
                          label="Quantity"
                          variant="outlined"
                          type="number"
                          fullWidth
                          value={currentPosition.quantity}
                          onChange={(e) => setCurrentPosition({ ...currentPosition, quantity: e.target.value })}
                          InputProps={{ endAdornment: <Box component="span" sx={{ ml: 1, color: DL.textMuted }}>Shares</Box> }}
                          sx={inputSx}
                        />
                      ) : currentPosition.type.includes("call") || currentPosition.type.includes("put") || currentPosition.type === "iron-condor" || currentPosition.type === "straddle" ? (
                        <Box>
                          <TextField
                            label="Contracts"
                            variant="outlined"
                            type="number"
                            fullWidth
                            value={currentPosition.quantity}
                            onChange={(e) => setCurrentPosition({ ...currentPosition, quantity: e.target.value })}
                            InputProps={{ endAdornment: <Box component="span" sx={{ ml: 1, color: DL.textMuted }}>Contracts</Box> }}
                            sx={inputSx}
                          />
                          <Typography sx={{ fontSize: "0.75rem", color: DL.textMuted, mt: 0.5 }}>
                            {currentPosition.type === "iron-condor"
                              ? "Each contract = 100 shares. Iron Condor requires 4 options contracts."
                              : currentPosition.type === "straddle"
                              ? "Each contract = 100 shares. Straddle requires 2 options contracts."
                              : "Each contract represents 100 shares."}
                          </Typography>
                        </Box>
                      ) : null}

                      {(currentPosition.type.includes("call") || currentPosition.type.includes("put")) && (
                        <TextField
                          label="Strike Price"
                          variant="outlined"
                          type="number"
                          fullWidth
                          value={currentPosition.strikePrice}
                          onChange={(e) => setCurrentPosition({ ...currentPosition, strikePrice: e.target.value })}
                          InputProps={{ startAdornment: <Box component="span" sx={{ mr: 1, color: DL.textMuted }}>$</Box> }}
                          sx={inputSx}
                        />
                      )}

                      {currentPosition.type === "iron-condor" && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                          <Typography sx={{ fontSize: "0.85rem", color: DL.textSecondary, fontWeight: 600 }}>Strike Prices</Typography>
                          {["lowerPutStrike", "upperPutStrike", "lowerCallStrike", "upperCallStrike"].map((field) => (
                            <TextField
                              key={field}
                              label={field.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase())}
                              variant="outlined"
                              type="number"
                              fullWidth
                              value={currentPosition[field]}
                              onChange={(e) => setCurrentPosition({ ...currentPosition, [field]: e.target.value })}
                              InputProps={{ startAdornment: <Box component="span" sx={{ mr: 1, color: DL.textMuted }}>$</Box> }}
                              sx={inputSx}
                            />
                          ))}
                        </Box>
                      )}

                      <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                        <Button
                          onClick={handleAddPosition}
                          disabled={!currentPosition.type || !currentPosition.ticker}
                          sx={primaryBtnSx}>
                          Add Position
                        </Button>
                        <Button onClick={() => setShowPositionForm(false)} sx={secondaryBtnSx}>
                          Cancel
                        </Button>
                      </Box>
                    </>
                  )}
                </Stack>
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  const isLastStep = activeStep === steps.length - 1;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: DL.bgDark,
        background: `radial-gradient(ellipse at 50% 0%, rgba(98,244,129,0.05) 0%, ${DL.bgDark} 55%)`,
        px: 2,
        py: isMobile ? 3 : 5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}>
      <ResponsiveTopBar isMobile={isMobile} onMobileMenuToggle={() => setMobileMenuOpen((prev) => !prev)} />
      <MobileNavDrawer open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {/* Card */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 800,
          mt: "64px",
          backgroundColor: DL.bgMedium,
          border: `1px solid ${DL.border}`,
          borderRadius: "12px",
          boxShadow: "0 4px 40px rgba(0,0,0,0.4)",
          overflow: "hidden",
        }}>
        {/* Header */}
        <Box sx={{ px: isMobile ? 3 : 4, py: 3, borderBottom: `1px solid ${DL.divider}` }}>
          <Typography sx={{ fontWeight: 800, fontSize: isMobile ? "1.3rem" : "1.6rem", color: DL.textPrimary }}>
            Create a New Post
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: DL.textMuted, mt: 0.25 }}>
            Step {activeStep + 1} of {steps.length}: {steps[activeStep]}
          </Typography>
        </Box>

        {/* Step progress bar */}
        <Box sx={{ px: isMobile ? 3 : 4, pt: 2.5, display: "flex", gap: 1 }}>
          {steps.map((label, i) => (
            <Box
              key={label}
              sx={{
                height: 4,
                flex: 1,
                borderRadius: 999,
                backgroundColor: i <= activeStep ? DL.green : "rgba(255,255,255,0.22)",
                transition: "background-color 0.3s ease",
              }}
            />
          ))}
        </Box>

        {/* Step content */}
        <Box sx={{ px: isMobile ? 3 : 4, pt: 3, pb: 1 }}>
          {getStepContent(activeStep)}
        </Box>

        {/* Actions */}
        <Box sx={{ px: isMobile ? 3 : 4, py: 3, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" gap={1}>
            <Button disabled={activeStep === 0} onClick={handleBack} sx={secondaryBtnSx}>
              Back
            </Button>
            {isLastStep ? (
              <Button onClick={handleSubmit} disabled={uploading} sx={primaryBtnSx}>
                {uploading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Submit Post"}
              </Button>
            ) : (
              <Button onClick={handleNext} disabled={!isStepValid()} sx={primaryBtnSx}>
                Continue
              </Button>
            )}
          </Stack>

        </Box>
      </Box>
    </Box>
  );
}

export default CreatePost;
